(function () {
  'use strict';

  // Run only on product detail pages. The product URL can also have a
  // prefix: /collections/{handle}/products/{handle} (opened from a
  // collection) or /{locale}/products/{handle} (markets / languages).
  if (!/(^|\/)products\/[^/]+/.test(window.location.pathname)) return;

  /*
   * Groups the color swatches of the main product (Swatch King).
   * Headings use <li class="ibkul-color-section-heading">, so the styles
   * in custom.css still apply.
   *
   * IMPORTANT: the "Color: …" label must NOT be display:none.
   * selly-color-sync.js reads jQuery(".swatch-variant-name:visible"); if the
   * label is hidden with display:none, Selly reads another label (e.g. the
   * size "XS") as the color. The label is therefore only visually hidden.
   */

  const ROOT_SELECTOR = 'variant-swatch-king';
  const COLOR_SELECTOR = '#swatch-option1[option-name="Color"]';
  const MAIN_SELECTOR = 'product-info[id^="MainProduct-"]';
  const HEADING_CLASS = 'ibkul-color-section-heading';
  const LABEL_CLASS = 'ibkul-selected-color';
  const HIDE_LABEL_CLASS = 'ibkul-hide-color-label';
  const STYLE_ID = 'ibkul-color-groups-style';

  const GROUPS = [
    { key: 'online', defaultLabel: 'LIMITED EDITION' },
    { key: 'core', defaultLabel: 'CORE COLORS' },
    { key: 'sale', defaultLabel: 'SALE COLORS' }
  ];

  const CSS = `
    .${HIDE_LABEL_CLASS} label.swatch-label {
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0 0 0 0) !important;
      clip-path: inset(50%) !important;
      white-space: nowrap !important;
      border: 0 !important;
    }
  `;

  const OBSERVE_OPTIONS = {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-checked']
  };

  let scheduled = false;
  let observer = null;

  /* ---------- Helpers ---------- */

  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    (document.head || document.documentElement).appendChild(style);
  }

  function toElement(node) {
    return node instanceof Element ? node : node && node.parentElement;
  }

  // True when a DOM change happens inside or around the main product.
  function touchesMainProduct(node) {
    const el = toElement(node);
    if (!el) return false;
    return Boolean(el.closest(MAIN_SELECTOR) || el.querySelector(MAIN_SELECTOR));
  }

  // Main product swatches only (not Selly, not product cards).
  function getColorOptions() {
    return Array.from(
      document.querySelectorAll(MAIN_SELECTOR + ' ' + ROOT_SELECTOR)
    )
      .map(root => root.querySelector(COLOR_SELECTOR))
      .filter(Boolean);
  }

  /* ---------- Data ---------- */

  function readJSON(id, fallback) {
    const element = document.getElementById(id);
    if (!element) return fallback;

    try {
      return JSON.parse(element.textContent || 'null') ?? fallback;
    } catch (error) {
      console.warn('[IBKÜL] Could not read metafield:', id, error);
      return fallback;
    }
  }

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function getColorList(id) {
    const values = readJSON(id, []);
    return Array.isArray(values)
      ? values.map(normalize).filter(Boolean)
      : [];
  }

  function getGroupData() {
    const rawLabels = readJSON('ibkul-color-group-labels', '');
    const parts = typeof rawLabels === 'string' ? rawLabels.split('|') : [];

    return {
      online: new Set(getColorList('ibkul-online-exclusive-colors')),
      sale: new Set(getColorList('ibkul-sale-colors')),
      hasCustomLabels:
        typeof rawLabels === 'string' && rawLabels.trim() !== '',
      labels: GROUPS.map((group, index) => {
        const custom = (parts[index] || '').trim();
        return custom || group.defaultLabel;
      })
    };
  }

  function getGroupForColor(value, data) {
    const color = normalize(value);
    if (data.online.has(color)) return 'online';
    if (data.sale.has(color)) return 'sale';
    return 'core';
  }

  // True when a swatch is hidden: CSS from the ibkul-hide-soldout-colors
  // snippet (applied from the first paint) or Swatch King's own inline
  // display: none.
  function isHiddenSwatch(item) {
    return window.getComputedStyle(item).display === 'none';
  }

  function getSelectedColor(list) {
    const selected = list.querySelector(
      'li.swatch-view-item[aria-checked="true"]'
    );
    const inner = selected && selected.querySelector('.swatch-selected');

    return (
      (selected && selected.getAttribute('orig-value')) ||
      (inner && inner.getAttribute('orig-value')) ||
      (inner && inner.getAttribute('data-value')) ||
      ''
    ).trim();
  }

  /* ---------- DOM updates ---------- */

  function groupColors(data, options) {
    // Regular product: no special colors and no custom labels.
    if (
      data.online.size === 0 &&
      data.sale.size === 0 &&
      !data.hasCustomLabels
    ) {
      options.forEach(option => {
        option
          .querySelectorAll('li.' + HEADING_CLASS)
          .forEach(heading => heading.remove());
      });
      return;
    }

    options.forEach(option => {
      const list = option.querySelector('ul.swatch-view');
      if (!list) return;

      const swatches = Array.from(list.children).filter(item =>
        item.matches('li.swatch-view-item')
      );
      if (!swatches.length) return;

      const buckets = { online: [], core: [], sale: [] };

      swatches.forEach(item => {
        const value =
          item.getAttribute('orig-value') ||
          item.getAttribute('data-value') ||
          '';
        buckets[getGroupForColor(value, data)].push(item);
      });

      const expected = [];

      GROUPS.forEach((group, index) => {
        const items = buckets[group.key];
        if (!items.length) return;

        // No heading for a group whose colors are all hidden (e.g. sold-out
        // colors hidden by the hide-soldout tags). The hidden swatches stay
        // in the list, but an empty heading is not shown.
        if (items.some(item => !isHiddenSwatch(item))) {
          expected.push({
            type: 'heading',
            key: group.key,
            label: data.labels[index]
          });
        }
        items.forEach(item => expected.push({ type: 'swatch', item }));
      });

      const current = Array.from(list.children);

      const alreadyGrouped =
        current.length === expected.length &&
        current.every((item, index) => {
          const entry = expected[index];
          if (entry.type === 'swatch') return item === entry.item;

          return (
            item.classList.contains(HEADING_CLASS) &&
            item.dataset.ibkulGroup === entry.key &&
            item.textContent.trim().startsWith(entry.label)
          );
        });

      if (alreadyGrouped) return;

      list
        .querySelectorAll('.' + HEADING_CLASS)
        .forEach(heading => heading.remove());

      const fragment = document.createDocumentFragment();

      expected.forEach(entry => {
        if (entry.type === 'swatch') {
          fragment.appendChild(entry.item);
          return;
        }

        const heading = document.createElement('li');
        heading.className = HEADING_CLASS;
        heading.dataset.ibkulGroup = entry.key;
        heading.setAttribute('role', 'presentation');
        heading.textContent = entry.label;
        fragment.appendChild(heading);
      });

      list.appendChild(fragment);
    });
  }

  function toggleOriginalColorLabel(data, options) {
    const hide = data.online.size > 0 || data.sale.size > 0;

    options.forEach(option => {
      if (option.classList.contains(HIDE_LABEL_CLASS) !== hide) {
        option.classList.toggle(HIDE_LABEL_CLASS, hide);
      }
    });
  }

  function updateSelectedColorLabels(data, options) {
    options.forEach(option => {
      const list = option.querySelector('ul.swatch-view');
      if (!list) return;

      const selectedColor = getSelectedColor(list);
      const selectedGroup = getGroupForColor(selectedColor, data);

      list.querySelectorAll('li.' + HEADING_CLASS).forEach(heading => {
        let label = heading.querySelector('.' + LABEL_CLASS);

        if (!label) {
          heading.appendChild(document.createTextNode(': '));
          label = document.createElement('span');
          label.className = LABEL_CLASS;
          heading.appendChild(label);
        }

        const showColor =
          Boolean(selectedColor) &&
          heading.dataset.ibkulGroup === selectedGroup;
        const text = showColor ? selectedColor : '';

        // Only write when changed — prevents a mutation loop.
        if (label.textContent !== text) label.textContent = text;
        if (label.hidden !== !showColor) label.hidden = !showColor;
      });
    });
  }

  /* ---------- Scheduling ---------- */

  function observe() {
    if (observer && document.body) observer.observe(document.body, OBSERVE_OPTIONS);
  }

  function scheduleGrouping() {
    if (scheduled) return;
    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;

      const options = getColorOptions();
      if (!options.length) return;

      const data = getGroupData();

      // Pause the observer while this script edits the DOM,
      // so our own changes do not retrigger it.
      if (observer) observer.disconnect();

      try {
        groupColors(data, options);
        toggleOriginalColorLabel(data, options);
        updateSelectedColorLabels(data, options);
      } finally {
        observe();
      }
    });
  }

  function start() {
    if (!document.body) return;

    injectStyle();

    // React only to changes in the main product: swatch re-renders
    // (childList) and selected color changes (aria-checked).
    observer = new MutationObserver(records => {
      if (records.some(record => touchesMainProduct(record.target))) {
        scheduleGrouping();
      }
    });

    observe();
    scheduleGrouping();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
